// The task drawer's sections (spec 06 §Boards and tasks "Task page"; Work OS task-detail.tsx):
// Subtasks (tasks whose parent is this one: complete, open, add, delete as a hover icon),
// Dependencies ("waiting on": hard ones block started and done statuses, soft ones only relate;
// add by searching tasks, no cycles), and the board's Fields (set or clear a value). Every change
// is one call; the server's problem (a cycle, a blocked status) is the toast.
import {
  Badge,
  Button,
  DrawerSection,
  Input,
  NativeSelect,
  NativeSelectOption,
  StatusGlyph,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LinkIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { AssigneeMenu, DueMenu } from "./inline-editors";
import { doneStatus, dueInstant, isDone, localDay, openStatus, type SeatPeople } from "./model";
import { DeleteTaskDialog, type DeletingTask } from "./task-delete";
import { useTaskWrites } from "./task-writes";
import type { Board, TaskDetail } from "./view-engine";
import { invalidateWork, subtasksQuery, taskSearchQuery, workKeys } from "./work-queries";

type Props = { api: Api; company: CompanyAccess; task: TaskDetail };

function useRefresh(company: CompanyAccess) {
  const queryClient = useQueryClient();
  return () => invalidateWork(queryClient, company.id);
}

const onEnter = (fn: () => void) => (e: KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter") {
    e.preventDefault();
    fn();
  }
};

/**
 * Linear's shape for an empty section: one subtle "+ Add …" row instead of a heading over an
 * empty box. The section opens when it has content or on a click, and stays open after; with
 * `onAdd` the click makes the content instead (a checklist), and the section shows once it exists.
 */
export function Collapsible({
  name,
  add,
  hasContent,
  onAdd,
  children,
}: {
  /** The section's accessible name ("Checklist"). */
  name: string;
  /** The row's text ("Add checklist"). */
  add: string;
  hasContent: boolean;
  onAdd?: () => void;
  children: ReactNode;
}) {
  const [opened, setOpened] = useState(false);
  if (hasContent || opened) return <>{children}</>;
  return (
    <section aria-label={name} className="mt-3 first:mt-0">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
        onClick={() => (onAdd ? onAdd() : setOpened(true))}
      >
        <PlusIcon aria-hidden="true" />
        {add}
      </Button>
    </section>
  );
}

export function SubtasksSection({
  api,
  company,
  task,
  board,
  onOpenTask,
  people,
}: Props & {
  board: Board | undefined;
  onOpenTask: (id: string) => void;
  /** Parity P1: with the seat people, each row edits its due date and assignee in place. */
  people?: SeatPeople;
}) {
  const co = company.id;
  const subtasks = useQuery(subtasksQuery(api, co, task.id));
  const [title, setTitle] = useState("");
  const [deleting, setDeleting] = useState<DeletingTask | null>(null);
  // WORK-PERF: a new sub-task is in the list at once; a tick shows before the answer.
  const writes = useTaskWrites(api, company);
  const add = {
    isPending: false,
    mutate: (t: string) => {
      writes.create({ board_id: task.board_id, title: t, parent_task_id: task.id });
      setTitle("");
    },
  };
  const complete = {
    mutate: (v: { id: string; status: string }) => writes.setStatus(v.id, v.status),
  };
  const rows = subtasks.data?.data ?? [];
  const done = rows.filter((t) => isDone(t, board)).length;
  const today = localDay(new Date());
  return (
    <Collapsible name="Subtasks" add="Add sub-task" hasContent={rows.length > 0}>
      <DrawerSection
        title={
          <span className="flex items-center gap-2">
            Subtasks
            {rows.length ? (
              <span className="text-xs font-normal text-muted-foreground tabular-nums">
                {done}/{rows.length} done
              </span>
            ) : null}
          </span>
        }
      >
        <ul aria-label="Subtasks" className="m-0 flex list-none flex-col p-0">
          {rows.map((t) => {
            const isDoneNow = isDone(t, board);
            const s = { id: t.id, title: t.title, done: isDoneNow };
            return (
              <li
                key={t.id}
                className="group/sub flex min-h-9 items-center gap-2.5 border-b border-subtle"
              >
                <button
                  type="button"
                  aria-pressed={isDoneNow}
                  aria-label={isDoneNow ? `Reopen ${t.title}` : `Complete ${t.title}`}
                  className="flex size-5 items-center justify-center rounded-full hover:text-success"
                  onClick={() => {
                    const status = isDoneNow ? openStatus(board) : doneStatus(board);
                    if (status) complete.mutate({ id: t.id, status });
                  }}
                >
                  <StatusGlyph task={s} />
                </button>
                <button
                  type="button"
                  className={
                    isDoneNow
                      ? "min-w-0 flex-1 truncate text-left text-sm text-muted-foreground line-through hover:underline"
                      : "min-w-0 flex-1 truncate text-left text-sm hover:underline"
                  }
                  onClick={() => onOpenTask(t.id)}
                >
                  {t.title}
                </button>
                {people ? (
                  <>
                    <DueMenu
                      task={t}
                      today={today}
                      overdue={Boolean(t.due_at && localDay(t.due_at) < today && !isDoneNow)}
                      onChange={(day) =>
                        writes.update(t.id, { due_at: day ? dueInstant(day) : null })
                      }
                    />
                    <AssigneeMenu
                      people={people}
                      seatIds={t.assignee_seat_ids}
                      title={t.title}
                      compact
                      onToggle={(seat, on) => writes.toggleAssignee(t.id, seat, on)}
                    />
                  </>
                ) : null}
                <span className="text-xs text-muted-foreground tabular-nums">#{t.number}</span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Delete ${t.title}`}
                  title="Delete"
                  className="opacity-0 group-hover/sub:opacity-100 group-focus-within/sub:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hover:text-destructive"
                  onClick={() => setDeleting({ id: t.id, number: t.number, title: t.title })}
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
              </li>
            );
          })}
        </ul>
        <Input
          aria-label="Add a subtask"
          placeholder="Add a subtask — Enter to add"
          className="mt-2"
          autoFocus={!rows.length}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onEnter(() => title.trim() && add.mutate(title.trim()))}
        />
        <DeleteTaskDialog
          api={api}
          company={company}
          task={deleting}
          onClose={() => setDeleting(null)}
        />
      </DrawerSection>
    </Collapsible>
  );
}

export function DependenciesSection({
  api,
  company,
  task,
  titleOf,
  onOpenTask,
}: Props & {
  /** The depended-on task's "#n title", when the caller knows it. */
  titleOf: (id: string) => string | null;
  onOpenTask: (id: string) => void;
}) {
  const refresh = useRefresh(company);
  const co = company.id;
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"hard" | "soft">("hard");
  const kindId = useId();
  const results = useQuery({ ...taskSearchQuery(api, co, q.trim()), enabled: q.trim().length > 1 });
  const add = useMutation({
    mutationFn: (id: string) =>
      api.client.tasks.addDependency({
        params: { co, id: task.id },
        body: { depends_on_task_id: id, kind },
      }),
    onSuccess: () => {
      setQ("");
      toast.add({
        title: kind === "hard" ? "Now waiting on that task" : "Linked",
        type: "success",
      });
    },
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.client.tasks.removeDependency({ params: { co, id: task.id, item_id: id } }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: workKeys.task(co, task.id) });
      void refresh();
    },
  });
  const taken = new Set([task.id, ...task.dependencies.map((d) => d.depends_on_task_id)]);
  const options = (results.data?.data ?? []).filter((t) => !taken.has(t.id)).slice(0, 6);
  return (
    <Collapsible name="Dependencies" add="Add dependency" hasContent={task.dependencies.length > 0}>
      <DrawerSection title="Dependencies">
        {task.dependencies.length ? (
          <ul aria-label="Waiting on" className="m-0 mb-2 flex list-none flex-col p-0">
            {task.dependencies.map((d) => (
              <li
                key={d.depends_on_task_id}
                className="flex min-h-9 items-center gap-2 border-b border-subtle"
              >
                <Badge variant={d.kind === "hard" ? "warning" : "neutral"}>
                  {d.kind === "hard" ? "Waiting on" : "Related"}
                </Badge>
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                  onClick={() => onOpenTask(d.depends_on_task_id)}
                >
                  {titleOf(d.depends_on_task_id) ?? "A task"}
                </button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove the dependency on ${titleOf(d.depends_on_task_id) ?? "this task"}`}
                  onClick={() => remove.mutate(d.depends_on_task_id)}
                >
                  <XIcon aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-2 text-sm text-muted-foreground">Not waiting on anything.</p>
        )}
        <div className="flex items-center gap-2">
          <Input
            aria-label="Search for a task this one depends on"
            placeholder="Add a task it depends on…"
            autoFocus={!task.dependencies.length}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label htmlFor={kindId} className="sr-only">
            Dependency kind
          </label>
          <NativeSelect
            id={kindId}
            className="w-36"
            value={kind}
            onChange={(e) => setKind(e.target.value as "hard" | "soft")}
          >
            <NativeSelectOption value="hard">Blocks it</NativeSelectOption>
            <NativeSelectOption value="soft">Related</NativeSelectOption>
          </NativeSelect>
        </div>
        {q.trim().length > 1 ? (
          <ul
            aria-label="Matching tasks"
            className="m-0 mt-1 flex list-none flex-col rounded-md border border-border p-1"
          >
            {options.length ? (
              options.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                    onClick={() => add.mutate(t.id)}
                  >
                    <LinkIcon aria-hidden="true" className="size-3.5 text-faint" />
                    <span className="text-xs text-muted-foreground tabular-nums">#{t.number}</span>
                    <span className="min-w-0 truncate">{t.title}</span>
                  </button>
                </li>
              ))
            ) : (
              <li className="px-2 py-1.5 text-sm text-muted-foreground">
                {results.isPending ? "Searching…" : "No other task matches."}
              </li>
            )}
          </ul>
        ) : null}
      </DrawerSection>
    </Collapsible>
  );
}

// Parity P1 wave 1: the board's fields moved to task-page/fields.tsx (editors by type, rules,
// history, the Agent group); `FieldsSection` stays importable from here.
export { FieldsSection } from "./task-page/fields";
